import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  FormControl,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Layout } from "../../components/layout";
import useAuth from "../../hooks/useAuth";
import { recuperarTodosPaineis } from "../../services/csp";
import {
  criarPerfil,
  editarPerfil,
  recuperarPerfilPorId,
} from "../../services/perfil";
import { Perfil, ListagemCsp } from "../../types";

const TelaPerfil: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [paineis, setPaineis] = useState<ListagemCsp[]>([]);

  const [perfil, setPerfil] = useState<Partial<Perfil>>({
    nome: "",
    valorCsp: "",
    url: "",
    porta: "",
    tipo: "SAT",
    bloqueado: "N",
    painel: { id: "" } as any,
  });

  const fetchPaineis = async () => {
    try {
      const response = await recuperarTodosPaineis();
      if (response) {
        setPaineis(response.data);
      }
    } catch (error) {
      toast.error("Erro ao recuperar painéis: " + error);
    }
  };

  const fetchPerfil = async () => {
    try {
      setIsLoading(true);
      const response = await recuperarPerfilPorId(id!);
      if (response?.data) {
        const data = response.data;
        setPerfil({
          nome: data.nome,
          valorCsp: data.valorCsp,
          url: data.url,
          porta: String(data.porta),
          tipo: data.tipo,
          bloqueado: data.bloqueado,
          painel: { id: String(data.painelId) } as any,
        });
      }
    } catch (error) {
      toast.error("Erro ao recuperar perfil: " + error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaineis();
    if (id) {
      fetchPerfil();
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!perfil.nome || !perfil.valorCsp || !perfil.url || !perfil.porta) {
      toast.error("Preencha todos os campos obrigatórios!");
      return;
    }

    if (!perfil.painel?.id) {
      toast.error("Selecione um painel!");
      return;
    }

    try {
      setIsLoading(true);

      const perfilData = {
        ...perfil,
        porta: String(perfil.porta),
        painel: { id: perfil.painel.id },
      };

      if (id) {
        await editarPerfil(id, perfilData as Perfil);
        toast.success("Perfil atualizado com sucesso!");
      } else {
        await criarPerfil(perfilData as Perfil);
        toast.success("Perfil criado com sucesso!");
      }
      navigate("/perfis");
    } catch (error) {
      toast.error("Erro ao salvar perfil: " + error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof Perfil, value: any) => {
    setPerfil((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePainelChange = (value: string) => {
    setPerfil((prev) => ({
      ...prev,
      painel: { id: value } as any,
    }));
  };

  if (isLoading && id) {
    return (
      <Layout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
        }}
      >
        <Container maxWidth="lg">
          <Card>
            <CardHeader
              title={id ? "Editar Perfil" : "Adicionar Perfil"}
              action={
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/perfis")}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : "Salvar"}
                  </Button>
                </Box>
              }
            />
            <CardContent>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Painel</InputLabel>
                      <Select
                        value={perfil.painel?.id || ""}
                        onChange={(e) => handlePainelChange(e.target.value)}
                        label="Painel"
                      >
                        {paineis.map((painel) => (
                          <MenuItem key={painel.id} value={painel.id}>
                            {painel.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nome"
                      required
                      value={perfil.nome}
                      onChange={(e) => handleChange("nome", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Valor CSP"
                      required
                      value={perfil.valorCsp}
                      onChange={(e) => handleChange("valorCsp", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="URL"
                      required
                      value={perfil.url}
                      onChange={(e) => handleChange("url", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Porta"
                      type="number"
                      required
                      value={perfil.porta}
                      onChange={(e) => handleChange("porta", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        value={perfil.tipo}
                        onChange={(e) => handleChange("tipo", e.target.value)}
                        label="Tipo"
                      >
                        <MenuItem value="SAT">SAT</MenuItem>
                        <MenuItem value="CABO">CABO</MenuItem>
                        <MenuItem value="IPTV">IPTV</MenuItem>
                        <MenuItem value="OUTROS">OUTROS</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={perfil.bloqueado}
                        onChange={(e) =>
                          handleChange("bloqueado", e.target.value)
                        }
                        label="Status"
                      >
                        <MenuItem value="N">Ativo</MenuItem>
                        <MenuItem value="S">Bloqueado</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Layout>
  );
};

export default TelaPerfil;
